import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import React, { useId, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Input } from 'design-system/src/components/Input/Input';
import { RadioGroup, RadioOption, RadioButton, RadioLabel } from 'design-system/src/components/Radio/Radio';
import { ModalGeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/ModalGeneralInfoTable/ModalGeneralInfoTable';
import { client, type Drug, type Patient, type Prescriber } from '../../../api';

const StyledInputWrapper = styled.div`
  margin-top: 8px;
  margin-left: 40px;
`;

export type CancelReason =
  // NOTE: these strings are used in python/partner_portal/blueprints/rx.py and need to be kept in sync
  'patient_declined' | 'sent_elsewhere' | 'duplicate' | 'prescription_corrected' | 'new_therapy' | 'other';

export type CancelReasonOption = { label: string; value: CancelReason };

type CancelPrescriptionModalProps = {
  closeModal: () => void;
  rxId: string;
  patient: Patient;
  drug: Drug;
  prescriber: Prescriber | null;
  prescribedOn: string | null;
};

const CancelPrescriptionModal = ({
  closeModal,
  rxId,
  patient,
  drug,
  prescriber,
  prescribedOn,
}: CancelPrescriptionModalProps) => {
  const [cancelReason, setCancelReason] = useState('');
  const [otherReasonText, setOtherReasonText] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const otherReasonInputRef = useRef<HTMLInputElement>(null);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "cancel prescription" for drug ID: ${drug.id}`);
        return;
      }
      const res = await client.post(`rxs/${drug.id}/cancel`, {
        cancel_reason: cancelReason,
        ...(cancelReason === 'other' && otherReasonText ? { other_cancel_reason_specified: otherReasonText } : {}),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rxs', rxId] });
      toast.custom(() => <Toast variant="success">Prescription has been canceled</Toast>);
      // navigate back to the previous page instead of prescriptions page to restore url query params
      navigate(-1);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const cancelOptions = [
    { label: 'The patient is switching therapies', value: 'new_therapy' },
    { label: 'No longer pursuing treatment', value: 'patient_declined' },
    { label: 'Completed the PA outside Tandem', value: 'sent_elsewhere' },
    { label: 'This is a duplicate prescription', value: 'duplicate' },
    { label: 'The prescription entered is incorrect', value: 'prescription_corrected' },
    { label: 'Other', value: 'other' },
  ];

  const radioGroupLabelId = useId();

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>Cancel prescription</ModalTitle>

      <ModalBody>
        <ModalGeneralInfoTable
          patientName={patient ? `${patient.first_name} ${patient.last_name}` : null}
          prescriberName={prescriber?.name || null}
          dob={patient?.date_of_birth || null}
          prescribedOn={prescribedOn || null}
          drugName={drug?.name || null}
          prescriptionId={drug?.id || null}
        />

        <Typography styledAs="bodyDMSans" id={radioGroupLabelId}>
          Tandem will stop processing this prescription and notify the patient of cancellation. Please select a reason
          for cancellation:
        </Typography>

        <div>
          <RadioGroup aria-labelledby={radioGroupLabelId}>
            {cancelOptions.map((reason) => (
              <RadioOption key={reason.value} htmlFor={reason.value}>
                <RadioButton
                  type="radio"
                  id={reason.value}
                  name="cancelReason"
                  value={reason.value}
                  checked={cancelReason === reason.value}
                  onChange={() => {
                    setCancelReason(reason.value as CancelReason);
                    if (reason.value !== 'other') {
                      setOtherReasonText('');
                    } else {
                      // Auto-focus the input when "other" is selected
                      setTimeout(() => {
                        otherReasonInputRef.current?.focus();
                      }, 0);
                    }
                  }}
                />
                <RadioLabel as="span">{reason.label}</RadioLabel>
              </RadioOption>
            ))}
          </RadioGroup>

          {cancelReason === 'other' && (
            <StyledInputWrapper>
              <Input
                ref={otherReasonInputRef}
                label="Please specify the reason for cancellation"
                hideLabel
                placeholder="Please specify"
                value={otherReasonText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOtherReasonText(e.target.value)}
              />
            </StyledInputWrapper>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal} disabled={submitMutation.isPending} size="sm">
          Back
        </Button>
        <Button
          onClick={() => submitMutation.mutate()}
          disabled={!cancelReason || (cancelReason === 'other' && !otherReasonText.trim()) || submitMutation.isPending}
          isLoading={submitMutation.isPending}
          size="sm"
        >
          Cancel prescription
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CancelPrescriptionModal;
